/**
 * RecipientInfo Component
 *
 * Displays recipient name and email.
 * Central information display area of the recipient card.
 * Channel badges have been moved to RecipientActions for better alignment.
 *
 * @module RecipientItem/RecipientInfo
 */

import { BlockStack, Icon, InlineStack, Text } from '@shopify/polaris';
import { EmailIcon } from '@shopify/polaris-icons';

export interface RecipientInfoProps {
  /** Recipient name */
  name: string;
  /** Recipient email address */
  email: string;
  /** Whether the recipient is verified */
  isVerified: boolean;
}

/**
 * Recipient information display component
 *
 * Shows the recipient's name and email.
 * Opacity is reduced when recipient is unverified.
 */
export function RecipientInfo({
  name,
  email,
  isVerified,
}: RecipientInfoProps) {
  // Build class names for verified/unverified state
  const containerClasses = [
    'recipient-info-container',
    !isVerified && 'recipient-info-container-unverified',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={containerClasses}>
      <BlockStack>
        <Text as="p" variant="bodyMd" fontWeight="semibold">
          {name}
        </Text>
        <InlineStack gap="100" blockAlign="center" wrap={false}>
          <div className="recipient-email-icon">
            <Icon source={EmailIcon} tone="subdued" />
          </div>
          <Text as="p" variant="bodySm" tone="subdued" truncate>
            {email}
          </Text>
        </InlineStack>
      </BlockStack>
    </div>
  );
}
